import * as React from "react";
import { Namespace, useT } from "@racoon/i18n";
import { Button } from "@racoon/ui";

/**
 * A pinned notice that something will happen once the countdown runs out, with
 * the one button that stops it. The caller owns the timer; this only shows how
 * much of it is left.
 */
export const UndoCountdownBar = ({
  message,
  deadline,
  durationMs,
  onUndo,
}: {
  message: string;
  /** `Date.now()` at which the action runs. */
  deadline: number;
  durationMs: number;
  onUndo: () => void;
}) => {
  const t = useT();
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(interval);
  }, []);

  const leftMs = Math.max(0, deadline - now);
  const seconds = Math.ceil(leftMs / 1000);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="undo-countdown-bar"
      // A floating surface like the toasts and popovers: white, hairline border, shadow-lg.
      className="fixed bottom-6 left-1/2 z-[1201] w-[min(480px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-xl border border-border-primary bg-white shadow-lg animate-in fade-in-0 slide-in-from-bottom-2"
    >
      <div className="flex items-center gap-3 p-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-assigned text-sm font-semibold tabular-nums text-text-information">
          {seconds}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-text-body">{message}</div>
          <div className="text-xs text-text-subtle">
            {t(Namespace.Routes, "selectedJobsUndoHint", { seconds })}
          </div>
        </div>
        {/* The tertiary «Отмена» of the confirmation dialogs. */}
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 text-text-information"
          data-testid="undo-countdown-cancel"
          onClick={onUndo}
        >
          {t(Namespace.Common, "cancel")}
        </Button>
      </div>
      <div className="h-1 bg-slate-100">
        <div
          className="h-full bg-surface-action"
          style={{ width: `${(leftMs / durationMs) * 100}%` }}
        />
      </div>
    </div>
  );
};
